import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import KuikaDrawnCheck, { KuikaDrawnCheckGlyph } from "../kuika-drawn-check/kuika-drawn-check";
import KuikaText from "../typography/Text/Text";
import "./kuika-checkbox.scss";

/** What the box shows: the tick, the "some, not all" bar, or nothing. */
type CheckboxGlyph = KuikaDrawnCheckGlyph | "none";

const KuikaCheckbox = ({
  label,
  checked: controlledChecked,
  onChange,
  disabled,
  defaultChecked,
  align = "center",
  labelPosition = "right",
  className,
  dataTestId,
  indeterminate,
  ariaLabel,
  id
}: {
  /**
   * A ReactNode rather than a string because some groups label an option with an icon plus
   * its name (the Desktop App pane's target platforms, for instance). A non-string label
   * carries no stable identifier, so `dataTestId` becomes required there.
   */
  label?: React.ReactNode;
  checked?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  defaultChecked?: boolean;
  align?: "center" | "top";
  labelPosition?: "right" | "left";
  className?: string;
  /**
   * Explicit test id for a checkbox with no label — the label-derived default reads
   * `undefinedCheckbox` there, which is neither addressable nor unique on a page with
   * more than one of them.
   */
  dataTestId?: string;
  /**
   * "Some, not all" — the state a select-all box is in while part of its group is ticked.
   * It is not a third value: `checked` still says what clicking it will do, and a box that
   * is checked outright ignores it.
   */
  indeterminate?: boolean;
  /**
   * Accessible name for a checkbox whose visible name is rendered outside the component
   * (e.g. a row label elsewhere in the layout), so there is no `label` prop to derive one from.
   */
  ariaLabel?: string;
  /** Forwarded onto the focusable `<input>`, so a `<label htmlFor>` targets it —
   * `KuikaFormField` clones this in from the form store. */
  id?: string;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [internalChecked, setInternalChecked] = useState(defaultChecked || false);
  const isControlled = controlledChecked !== undefined;
  const checked = isControlled ? controlledChecked : internalChecked;

  const handleCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;
    if (!isControlled) {
      setInternalChecked(newChecked);
    }
    if (onChange) {
      onChange(event);
    }
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    if (!disabled && e.target !== e.currentTarget.querySelector("input")) {
      handleCheckedChange({ target: { checked: !checked } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  // The label-derived ids below are the historical contract (`${label}Checkbox`), so they
  // survive only while the label IS a string. A node-labelled checkbox falls back to
  // `dataTestId`, which is why that prop exists.
  const labelText: string | undefined = typeof label === "string" ? label : undefined;

  // `indeterminate` exists only on the DOM node, never as an attribute.
  const isIndeterminate: boolean = !!indeterminate && !checked;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  let glyph: CheckboxGlyph = "none";
  if (checked) {
    glyph = "check";
  } else if (isIndeterminate) {
    glyph = "dash";
  }

  // The glyph the last change turned on, derived from the previous one during render (React's
  // documented pattern, as the filter chips do). Only that glyph draws itself in; the first
  // render turns nothing on, so a list that opens with fifty ticked boxes does not draw fifty ticks.
  const [previousGlyph, setPreviousGlyph] = useState<CheckboxGlyph>(glyph);
  const [turnedOnGlyph, setTurnedOnGlyph] = useState<CheckboxGlyph>("none");
  if (glyph !== previousGlyph) {
    setPreviousGlyph(glyph);
    setTurnedOnGlyph(glyph);
  }

  const wrapperClassName = classNames("kuika-checkbox", className, {
    "kuika-checkbox--center": align === "center",
    "kuika-checkbox--top": align === "top"
  });

  const inputClassName = classNames("kuika-checkbox__root", {
    "kuika-checkbox--checked": checked,
    "kuika-checkbox--indeterminate": isIndeterminate,
    "kuika-checkbox--disabled": disabled
  });

  const inputWrapperClassName = classNames("kuika-checkbox__input-wrapper", {
    "kuika-checkbox__input-wrapper--disabled": disabled
  });

  const renderGlyph = (kind: KuikaDrawnCheckGlyph, className: string): React.ReactNode => (
    <KuikaDrawnCheck
      glyph={kind}
      draw={turnedOnGlyph === kind}
      className={classNames("kuika-checkbox__glyph", className)}
    />
  );

  return (
    <div className={wrapperClassName}>
      {labelPosition === "left" && label && <KuikaText className="kuika-checkbox__label">{label}</KuikaText>}
      <div className={inputWrapperClassName} onClick={handleWrapperClick}>
        <input
          ref={inputRef}
          id={id}
          type="checkbox"
          className={inputClassName}
          checked={checked}
          onChange={handleCheckedChange}
          disabled={disabled}
          data-testid={dataTestId ?? (labelText === undefined ? undefined : `${labelText}Checkbox`)}
          data-onboarding-id={labelText === undefined ? undefined : `ui-kit_checkbox_${labelText}`}
          aria-label={ariaLabel}
        />
        {glyph === "check" && renderGlyph("check", "kuika-checkbox__checkmark")}
        {glyph === "dash" && renderGlyph("dash", "kuika-checkbox__dash")}
      </div>
      {labelPosition === "right" && label && (
        <KuikaText
          data-testid={labelText === undefined ? undefined : `${labelText}Label`}
          data-onboarding-id={labelText === undefined ? undefined : `ui-kit_checkbox_${labelText}-label`}
          className="kuika-checkbox__label"
        >
          {label}
        </KuikaText>
      )}
    </div>
  );
};

export default KuikaCheckbox;
